import React, { useEffect, useState } from 'react';
import { CircularProgress, Avatar, Dialog, DialogContent, IconButton } from '@mui/material';
import CakeIcon from '@mui/icons-material/Cake';
import GroupIcon from '@mui/icons-material/Group';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CloseIcon from '@mui/icons-material/Close';
import birthdayServices from '../../services/birthday.service';
import UserModal from '../RecognitionModal/UserModal';
import { getDisplayNameFromEmail } from '../../utils/userUtils';
import { formatBirthdayDate, getBirthdayTiming } from '../../utils/dateUtils';
import styles from './birthday.module.css';

const BirthdayCalendar = ({ open, onClose }) => {
  const [todayBirthdays, setTodayBirthdays] = useState([]);
  const [weekBirthdays, setWeekBirthdays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const dialogStyle = {
    '& .MuiDialog-paper': {
      maxWidth: '1200px',
      width: '95vw',
      margin: '24px',
      borderRadius: '12px',
      maxHeight: '90vh',
    },
  };

  useEffect(() => {
    const fetchBirthdays = async () => {
      if (open) {  // Only fetch when dialog is open
        try {
          setLoading(true);
          setError(null);
          const response = await birthdayServices.getAllBirthdays();
          setTodayBirthdays(response.today || []);
          setWeekBirthdays(response.week || []);
        } catch (error) {
          console.error('Failed to fetch birthdays:', error);
          setError('Failed to load birthdays. Please try again later.');
          setTodayBirthdays([]);
          setWeekBirthdays([]);

        } finally {
          setLoading(false);
        }
      }
    };

    fetchBirthdays();
  }, [open]); // Re-fetch when dialog opens

  const handleUserClick = (user) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const renderBirthdaySection = (users, title) => (
    <div className={styles.birthdaySection}>
      <h3>
        <CakeIcon fontSize="small" />
        {title}
      </h3>
      {users.length === 0 ? (
        <div className={styles.noDataMessage}>
          No birthdays {title.toLowerCase().includes('today') ? 'today' : 'this week'}
        </div>
      ) : (
        users.map((user) => (
          <div
            key={user.username}
            className={styles.birthdayItem}
            onClick={() => handleUserClick(user)}
          >
            <div className={styles.avatarContainer}>
              <Avatar
                src={user.profilePictureUrl}
                alt={user.preferredName}
                sx={{
                  width: 36,
                  height: 36,
                  backgroundColor: !user.profilePictureUrl ? '#1d9f9f' : undefined,
                }}
              >
                {!user.profilePictureUrl && (user.preferredName || 'U').charAt(0).toUpperCase()}
              </Avatar>
            </div>
            <div className={styles.userInfo}>
              <div className={styles.userName}>
                {user.preferredName || getDisplayNameFromEmail(user.email)}
              </div>
              <div className={styles.userDetails}>
                {user.city && (
                  <>
                    <LocationOnIcon fontSize="small" />
                    <span>{user.city}</span>
                  </>
                )}
                {user.groups?.length > 0 && (
                  <>
                    <GroupIcon fontSize="small" />
                    <span>{user.groups.join(', ')}</span>
                  </>
                )}
              </div>
            </div>
            <div className={styles.birthdayDate}>
              <CakeIcon fontSize="small" />
              {formatBirthdayDate(user.birthday)}
            </div>
          </div>
        ))
      )}
    </div>
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth sx={dialogStyle}>
      <DialogContent className={styles.dialogContent}>
        <div className={styles.dialogHeader}>
          <h2>Birthday Calendar</h2>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>

        <div className={styles.birthdayContent}>
          {loading ? (
            <div className={styles.loadingContainer}>
              <CircularProgress size={40} />
              <p>Loading birthdays...</p>
            </div>
          ) : error ? (
            <div className={styles.errorContainer}>
              <p>{error}</p>
            </div>
          ) : (
            <>
              {renderBirthdaySection(todayBirthdays, "Today's Birthdays")}
              {renderBirthdaySection(weekBirthdays, "This Week's Birthdays")}

              {todayBirthdays.length === 0 && weekBirthdays.length === 0 && (
                <div className={styles.noDataMessage}>
                  <h3>No upcoming birthdays this week</h3>
                </div>
              )}

              <div className={styles.updateProfileHint}>
                <p>Want your birthday to appear here? Please update your profile information.</p>
              </div>
            </>
          )}
        </div>
        </DialogContent>

      <UserModal
        user={selectedUser}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        variant="birthday"
        birthdayTiming={selectedUser ? getBirthdayTiming(selectedUser.birthday) : 'upcoming'}
      />
    </Dialog>
  );
};

export default BirthdayCalendar;
